import React, { useEffect, useMemo, useState } from "react";

import { Box, Checkbox, Divider, FormControlLabel, Grid, TextField, Typography } from "@material-ui/core";

import { CloudUploadRounded } from "@material-ui/icons";
import { AutocompleteFilter } from "@remar/shared/dist/components/AutocompleteFilter";
import Button from "@remar/shared/dist/components/Button";
import Uploader from "@remar/shared/dist/components/Uploader";
import { Wrapper } from "@remar/shared/dist/layouts";

import { ILocation, LiveStream } from "@remar/shared/dist/models";
import { ContentAccessTypes } from "@remar/shared/dist/models/courseChapter.model";
import { useFormik } from "formik";
import { Location } from "institutions/src/store/services";
import isEmpty from "lodash/isEmpty";
import { useDispatch, useSelector } from "react-redux";

import { useHistory, useParams } from "react-router-dom";
import { Subject } from "rxjs";
import {
	createLiveStream,
	fetchLiveStream,
	getLiveStreamFullState,
	uploadAttachment
} from "store/features/LiveStream/LiveStream.slice";
import { fetchLocations, selectManageLocationsFullState } from "store/features/ManageLocations/manageLocations.slice";

import { emit } from "store/features/notifications/notifications.slice";
import * as Yup from "yup";

import { routes } from "core/constants";
import { InputField } from "modules/Components/InputField";

import { CreateStreamWrapper } from "./styles";

import { StyledLocationIcon } from "../ManageStudents/styles";

const breadcrumb = [
	{ title: "Live Stream", key: 0, link: routes.stream.getPath() },
	{ title: "Create Live Stream", key: 1 }
];

type IFormValues = {
	allLocations: boolean;
	description: string;
	name: string;
	paidAudience: boolean;
	selectedLocations: Array<ILocation>;
	trialAudience: boolean;
};

const validationSchema = Yup.object({
	allLocations: Yup.boolean(),
	description: Yup.string(),
	name: Yup.string().required("Required"),
	paidAudience: Yup.boolean().test("at-least-one", "At least one audience type must be selected", function (v) {
		const { trialAudience } = this.parent;
		if (!v && !trialAudience) {
			return this.createError({ path: "allAudience", message: "At least one audience type must be selected" });
		}
		return true;
	}),
	selectedLocations: Yup.array()
		.of(Yup.object().shape({ id: Yup.number(), name: Yup.string() }))
		.when("allLocations", {
			is: false,
			then: s => s.ensure().min(1, "At least one location type must be selected"),
			otherwise: s => s.optional()
		}),
	trialAudience: Yup.boolean().test("at-least-one", "At least one audience type must be selected", function (v) {
		const { paidAudience } = this.parent;
		if (!paidAudience && !v) {
			return this.createError({ path: "allAudience", message: "At least one audience type must be selected" });
		}
		return true;
	})
});

const LOCATION_PER_PAGE = 20;
const initVals: IFormValues = {
	allAudience: false,
	allLocations: false,
	description: "",
	name: "",
	paidAudience: false,
	selectedLocations: [],
	trialAudience: false
};
const allowedFormats = [".jpeg", ".jpe", ".jpg", ".png"];

const StartStream = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const { location } = history;
	const { id } = useParams<{ id: string }>();
	const { locations, page: locationPage } = useSelector(selectManageLocationsFullState);
	const { isCreatingStream, mainImageKey: mainImgageStateKey } = useSelector(getLiveStreamFullState);
	const clearFileInputSubject = new Subject<void>();
	const [attachmentProgress, setAttachmentProgress] = useState(0);
	const [attachmentLoading, setAttachmentLoading] = useState(false);
	const [attachmentUploadError, setAttachmentUploadError] = useState(false);

	useEffect(() => {
		if (!location?.state?.stream && !!id) {
			dispatch(fetchLiveStream({ id }));
		}
	}, [dispatch, id, location?.state?.stream]);

	useEffect(() => {
		dispatch(fetchLocations({ perPage: 10, page: 1, searchKeyword: "", filters: {} }));
	}, [dispatch]);

	const initialValues: IFormValues = useMemo(() => {
		if (location?.state?.stream) {
			const { contentAccessTypeId, allowedLocations, allLocations, name, description } = location.state
				.stream as LiveStream;

			let allAudience = false;
			let paidAudience = false;
			let trialAudience = false;

			switch (contentAccessTypeId) {
				case ContentAccessTypes.Mixed:
					allAudience = true;
					paidAudience = true;
					trialAudience = true;
					break;
				case ContentAccessTypes.Paid:
					allAudience = false;
					paidAudience = true;
					trialAudience = false;
					break;
				case ContentAccessTypes.Trial:
					allAudience = false;
					trialAudience = true;
					paidAudience = false;
					break;
			}

			return {
				allAudience,
				allLocations,
				description,
				name,
				paidAudience,
				selectedLocations: allowedLocations,
				trialAudience
			};
		}
		return initVals;
	}, [location?.state?.stream]);

	const mainImageKey = location?.state?.stream?.mainImageKey || mainImgageStateKey;

	const {
		values,
		handleChange,
		isSubmitting,
		handleBlur,
		errors,
		touched,
		setFieldValue,
		handleSubmit,
		dirty,
		isValid
	} = useFormik({
		initialValues: initialValues,
		validationSchema,
		validateOnChange: true,
		validateOnBlur: true,
		onSubmit: async ({ name, description, paidAudience, allLocations, selectedLocations }) => {
			let contentAccessTypeId;
			if (paidAudience && trialAudience) {
				contentAccessTypeId = ContentAccessTypes.Mixed;
			} else if (paidAudience) {
				contentAccessTypeId = ContentAccessTypes.Paid;
			} else {
				contentAccessTypeId = ContentAccessTypes.Trial;
			}

			const data = {
				id,
				name,
				description,
				startTime: new Date().toISOString(),
				contentAccessTypeId,
				allLocations,
				mainImageKey
			};

			if (selectedLocations?.length > 0) {
				data["allLocations"] = false;
				data["locationIds"] = (selectedLocations as Location[]).map(location => ({ value: location.id }));
			}

			dispatch(
				createLiveStream({
					data,
					cb: stream => {
						id ? history.goBack() : history.push(`${routes.stream.join.getPath()}/${stream.id}/${stream.streamId}`);
					}
				})
			);
		}
	});

	useEffect(() => {
		if (!isEmpty(errors) && isSubmitting) {
			const message = Object.values(errors)
				.map(e => e)
				.join("\n, ");
			dispatch(emit({ message, color: "error" }));
		}
	}, [dispatch, errors, isSubmitting]);

	const { name, description, selectedLocations, paidAudience, trialAudience, allLocations } = values;

	const handleUpload = async (file: Partial<File>): Promise<void> => {
		const methods = {
			clearFileInput: () => clearFileInputSubject.next(),
			progress: setAttachmentProgress,
			uploadError: setAttachmentUploadError
		};
		setAttachmentLoading(true);

		dispatch(
			uploadAttachment({
				file,
				options: {
					onError: () => {
						setAttachmentLoading(false);
						methods.uploadError(true);
					},
					onProgress: ({ loaded, total }) => {
						methods.progress((loaded / total) * 100);
					},
					onUploaded: () => {
						methods.clearFileInput();
						setAttachmentLoading(false);
					}
				}
			})
		);
	};

	return (
		<form onSubmit={handleSubmit}>
			<Wrapper
				heading="Create Stream"
				breadcrumb={breadcrumb}
				actions={
					<Box display="flex" gridGap={5}>
						<Button variant="filled" color="secondary" onClick={() => history.goBack()}>
							Cancel
						</Button>
						<Button
							variant="filled"
							color="primary"
							type="submit"
							disabled={isCreatingStream || !mainImageKey || !dirty || !isValid}
							loading={isCreatingStream}
						>
							{location?.state?.stream ? "Update" : "Go Live Now"}
						</Button>
					</Box>
				}
			>
				<CreateStreamWrapper>
					<Box className={"headerWrapper"}>
						<Typography className={"header"} color={"textSecondary"}>
							Live Stream Configuration
						</Typography>
					</Box>
					<Box className={"configWrapper"}>
						<Grid container={true} spacing={2}>
							<Grid item xs={9}>
								<InputField
									title="Live Stream Name"
									inputComponent={
										<TextField
											fullWidth
											hiddenLabel
											InputProps={{ style: { color: "primary" }, disableUnderline: true }}
											inputProps={{ maxLength: 140 }}
											color="primary"
											id="name"
											disabled={!!id}
											name="name"
											onChange={handleChange}
											onBlur={handleBlur}
											variant="filled"
											size="small"
											error={!!errors.name && touched.name}
											helperText={!!errors.name && touched.name}
											placeholder="Name"
											value={name}
										/>
									}
								/>
								<InputField
									title="Live Description (Optional)"
									inputComponent={
										<TextField
											fullWidth
											hiddenLabel
											InputProps={{ style: { color: "primary" }, disableUnderline: true }}
											inputProps={{ maxLength: 140 }}
											color="primary"
											id="description"
											disabled={!!id}
											name="description"
											onChange={handleChange}
											onBlur={handleBlur}
											variant="filled"
											size="small"
											error={!!errors.description && touched.description}
											helperText={!!errors.description && touched.description}
											placeholder="Description"
											value={description}
										/>
									}
								/>{" "}
								<InputField
									title="Audience"
									inputComponent={
										<Box display="flex">
											<FormControlLabel
												label="All"
												control={
													<Checkbox
														checked={paidAudience && trialAudience}
														name="allAudience"
														onChange={e => {
															setFieldValue("paidAudience", e.target.checked, true);
															setFieldValue("trialAudience", e.target.checked, true);
														}}
														color="primary"
													/>
												}
											/>
											<Divider orientation="vertical" flexItem className={"divider"} />
											<FormControlLabel
												label="Paid"
												control={
													<Checkbox
														checked={paidAudience}
														name="paidAudience"
														onChange={e => {
															handleChange(e);
															if (!e.target.checked) {
																setFieldValue("allAudience", false);
															} else if (values.trialAudience) {
																setFieldValue("allAudience", true);
															}
														}}
														onBlur={handleBlur}
														color="primary"
													/>
												}
											/>
											<FormControlLabel
												label="Trial"
												control={
													<Checkbox
														name="trialAudience"
														checked={trialAudience}
														onChange={e => {
															handleChange(e);
															if (!e.target.checked) {
																setFieldValue("allAudience", false);
															} else if (values.paidAudience) {
																setFieldValue("allAudience", true);
															}
														}}
														onBlur={handleBlur}
														color="primary"
													/>
												}
											/>
											<FormControlLabel
												label="All Locations"
												control={
													<Checkbox
														name="allLocations"
														style={{ marginLeft: 16 }}
														checked={allLocations}
														onChange={e => {
															handleChange(e);
															if (e.target.checked) {
																setFieldValue("selectedLocations", [], true);
															}
														}}
														onBlur={handleBlur}
														color="primary"
													/>
												}
											/>
											<AutocompleteFilter
												options={locations}
												value={selectedLocations || []}
												disabled={values.allLocations}
												setValue={v => {
													setFieldValue("selectedLocations", v);
													if (v.length > 0) {
														setFieldValue("allLocations", false);
													}
												}}
												filterName="Locations"
												filterIcon={<StyledLocationIcon />}
												noOptionsText="No Location Selected"
												loadMoreResults={() => {
													dispatch(
														fetchLocations({
															perPage: LOCATION_PER_PAGE,
															page: locationPage + 1,
															searchKeyword: "",
															filters: {},
															infinite: true
														})
													);
												}}
												onTextInputChange={text => {
													dispatch(
														fetchLocations({
															perPage: LOCATION_PER_PAGE,
															page: 1,
															searchKeyword: text,
															filters: {}
														})
													);
												}}
											/>
										</Box>
									}
								/>
							</Grid>
							<Grid item xs={3}>
								{!!id ? (
									<img
										style={{ float: "right" }}
										alt="preview image"
										width={250}
										height={250}
										src={location?.state?.stream.mainImageUrl}
									/>
								) : (
									<Uploader
										name="mainImageKey"
										accept={allowedFormats.join(",")}
										clearFileInputSubject={clearFileInputSubject}
										error={attachmentUploadError}
										header="Upload Photo"
										value={mainImageKey}
										// header={logo ? "" : "Upload Logo"}
										onRemove={() => {
											setFieldValue("mainImageKey", undefined);
											clearFileInputSubject.next();
										}}
										icon={<CloudUploadRounded name="cloud-upload" style={{ width: "50px", height: "50px" }} />}
										loading={attachmentLoading}
										onAdd={files => handleUpload(files[0])}
										progress={attachmentProgress}
										dragFileError="File type is not allowed."
										title="Drag and Drop File Here or Browse to Choose a File. JPEG, JGP, PNG types are allowed. Recommended Size: 128x128"
										confirmDescription="Are you sure you want to remove this logo?"
										emit={emit}
									/>
								)}
							</Grid>
						</Grid>
					</Box>
				</CreateStreamWrapper>
			</Wrapper>
		</form>
	);
};

export default StartStream;
